import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { connectToContract } from "./CrownContract";
import CUSDABI from "../abi/CUSD.json";
import CUSDMintABI from "../abi/CUSDMint.json";
import DEUSABI from "../abi/DEUS.json";
import ChainlinkPriceFeedABI from "../abi/ChainlinkPriceFeed.json";
import { FaWallet, FaDollarSign, FaEuroSign, FaCoins, FaChartLine, FaLock, FaExchangeAlt } from "react-icons/fa";
import { MdTrendingUp } from "react-icons/md";
import { formatEther } from "ethers";
import ChartComponent from "./ChartComponent";

const cusdAddress = "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa";
const cusdMintAddress = "0xaE0ADC7D373cC41b32211915640175bC08e21e1B"; // Sjednoceno s CUSD.js, změňte na 0xf5dBf5e564c9f5C28759CD64aE9C2AeDb07b5659, pokud je potřeba
const deusAddress = "0x9a1EE4cC684b75eb847F2CCBDf7DeB26F8f8D6F5";


const Overview = ({ provider, account }) => {
  const [crownData, setCrownData] = useState({
    balance: "0",
    contractBalance: "0",
    totalSupply: "0",
    totalSoldTokens: "0",
    reserveETH: "0",
    reserveTokens: "0",
    collateralReserveETH: "0",
    rewardPool: "0",
    tokenPriceBase: "0",
    oraclePrice: "Není dostupná",
    priceSource: "Fixed",
  });
  const [cusdData, setCUSDData] = useState({
    balance: "0",
    totalSupply: "0",
    collateralETH: "0",
    collateralDAI: "0",
    collateralUSDC: "0",
    fixedPrice: "0",
    oraclePrice: "Není dostupná",
    priceSource: "Fixed",
    isAuthorized: false,
    authorizationFeeUSD: "0",
    mintFeePercent: "0",
  });
  const [deusData, setDEUSData] = useState({
    balance: "0",
    totalSupply: "0",
    fixedPrice: "0",
    oraclePrice: "Není dostupná",
    priceSource: "Fixed",
  });
  const [priceHistory, setPriceHistory] = useState({ labels: [], values: [] });
  const [network, setNetwork] = useState(null);
  const [contractExists, setContractExists] = useState(null);

  useEffect(() => {
    const fetchNetworkAndContract = async () => {
      if (provider) {
        try {
          const networkInfo = await provider.getNetwork();
          console.log("Overview.js - Network:", networkInfo);
          setNetwork(networkInfo);

          const code = await provider.getCode(deusAddress);
          console.log("Overview.js - DEUS Contract Code at address:", code);
          setContractExists(code !== "0x" && code !== "0x0");

          // Kontrola nasazení CUSDMint kontraktu
          const cusdMintCode = await provider.getCode(cusdMintAddress);
          console.log("Overview.js - CUSDMint Contract Code at address:", cusdMintAddress, "Code:", cusdMintCode);
          if (cusdMintCode === "0x" || cusdMintCode === "0x0") {
            console.error("Overview.js - CUSDMint contract not deployed at address:", cusdMintAddress);
          }
        } catch (err) {
          console.error("Overview.js - Error fetching network or contract code:", err);
        }
      }
    };
    fetchNetworkAndContract();
  }, [provider]);

  useEffect(() => {
    const fetchData = async () => {
      if (!provider || !account) {
        console.log("Overview.js - Provider or account not available. Provider:", provider, "Account:", account);
        return;
      }

      console.log("Overview.js - Provider:", provider);
      console.log("Overview.js - Account:", account);
      console.log("Overview.js - DEUS Address:", deusAddress);
      console.log("Overview.js - CUSD Address:", cusdAddress);
      console.log("Overview.js - CUSDMint Address:", cusdMintAddress);

      // Crown data
      const crownContract = await connectToContract(provider);
      try {
        const [
          crownBalance,
          contractBalance,
          crownTotalSupply,
          totalSoldTokens,
          reserveETH,
          reserveTokens,
          collateralReserveETH,
          rewardPool,
          tokenPriceBase,
          useOraclePrice,
        ] = await Promise.all([
          crownContract.balanceOf(account).catch(err => {
            console.error("Overview.js - Error fetching Crown balance:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.getContractBalance().catch(err => {
            console.error("Overview.js - Error fetching contract balance:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.totalSupply().catch(err => {
            console.error("Overview.js - Error fetching Crown totalSupply:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.totalSoldTokens().catch(err => {
            console.error("Overview.js - Error fetching totalSoldTokens:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.reserveETH().catch(err => {
            console.error("Overview.js - Error fetching reserveETH:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.reserveTokens().catch(err => {
            console.error("Overview.js - Error fetching reserveTokens:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.collateralReserveETH().catch(err => {
            console.error("Overview.js - Error fetching collateralReserveETH:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.rewardPool().catch(err => {
            console.error("Overview.js - Error fetching rewardPool:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.tokenPriceBase().catch(err => {
            console.error("Overview.js - Error fetching tokenPriceBase:", err);
            return ethers.toBigInt(0);
          }),
          crownContract.useOraclePrice().catch(err => {
            console.error("Overview.js - Error fetching useOraclePrice:", err);
            return false;
          }),
        ]);

        const oraclePrice = useOraclePrice
          ? "Není dostupná z kontraktu"
          : formatEther(tokenPriceBase);
        const priceSource = useOraclePrice ? "Oracle" : "Fixed";

        setCrownData({
          balance: formatEther(crownBalance),
          contractBalance: formatEther(contractBalance),
          totalSupply: formatEther(crownTotalSupply),
          totalSoldTokens: formatEther(totalSoldTokens),
          reserveETH: formatEther(reserveETH),
          reserveTokens: formatEther(reserveTokens),
          collateralReserveETH: formatEther(collateralReserveETH),
          rewardPool: formatEther(rewardPool),
          tokenPriceBase: formatEther(tokenPriceBase),
          oraclePrice: oraclePrice,
          priceSource: priceSource,
        });
      } catch (error) {
        console.error("Overview.js - Error fetching Crown data:", error);
      }

      // CUSD and CUSDMint data
      try {
        const signer = await provider.getSigner();
        console.log("Overview.js - Signer address:", await signer.getAddress());
        const cusdContract = new ethers.Contract(cusdAddress, CUSDABI, signer);
        const cusdMintContract = new ethers.Contract(cusdMintAddress, CUSDMintABI, signer);

        const [
          cusdBalance,
          cusdTotalSupply,
          collateralETH,
          collateralDAI,
          collateralUSDC,
          fixedPrice,
          useCUSDOracle,
          isAuthorized,
          authorizationFeeUSD,
          mintFeePercent,
        ] = await Promise.all([
          cusdContract.balanceOf(account).catch(err => {
            console.error("Overview.js - Error fetching CUSD balance:", err);
            return ethers.toBigInt(0);
          }),
          cusdContract.totalSupply().catch(err => {
            console.error("Overview.js - Error fetching CUSD totalSupply:", err);
            return ethers.toBigInt(0);
          }),
          cusdContract.collateral(account, await cusdContract.ETH_TOKEN().catch(err => {
            console.error("Overview.js - Error fetching ETH_TOKEN:", err);
            return ethers.ZeroAddress;
          })).catch(err => {
            console.error("Overview.js - Error fetching collateralETH:", err);
            return ethers.toBigInt(0);
          }),
          cusdContract.collateral(account, await cusdContract.DAI().catch(err => {
            console.error("Overview.js - Error fetching DAI:", err);
            return ethers.ZeroAddress;
          })).catch(err => {
            console.error("Overview.js - Error fetching collateralDAI:", err);
            return ethers.toBigInt(0);
          }),
          cusdContract.collateral(account, await cusdContract.USDC().catch(err => {
            console.error("Overview.js - Error fetching USDC:", err);
            return ethers.ZeroAddress;
          })).catch(err => {
            console.error("Overview.js - Error fetching collateralUSDC:", err);
            return ethers.toBigInt(0);
          }),
          cusdContract.CUSD_PRICE().catch(err => {
            console.error("Overview.js - Error fetching CUSD_PRICE:", err);
            return ethers.toBigInt(0);
          }),
          cusdContract.useCUSDOracle().catch(err => {
            console.error("Overview.js - Error fetching useCUSDOracle:", err);
            return false;
          }),
          cusdMintContract.authorizedMinters(account).catch(err => {
            console.error("Overview.js - Error fetching authorizedMinters:", err);
            return false;
          }),
          cusdMintContract.authorizationFeeUSD().catch(err => {
            console.error("Overview.js - Error fetching authorizationFeeUSD:", err);
            return ethers.toBigInt(0);
          }),
          cusdMintContract.mintFeePercent().catch(err => {
            console.error("Overview.js - Error fetching mintFeePercent:", err);
            return ethers.toBigInt(0);
          }),
        ]);

        console.log("Overview.js - Fetched CUSD data:", {
          cusdBalance,
          cusdTotalSupply,
          collateralETH,
          collateralDAI,
          collateralUSDC,
          fixedPrice,
          useCUSDOracle,
          isAuthorized,
          authorizationFeeUSD,
          mintFeePercent,
        });

        const oraclePrice = useCUSDOracle
          ? "Není dostupná z kontraktu"
          : formatEther(fixedPrice);
        const priceSource = useCUSDOracle ? "Oracle" : "Fixed";

        setCUSDData({
          balance: formatEther(cusdBalance),
          totalSupply: formatEther(cusdTotalSupply),
          collateralETH: formatEther(collateralETH),
          collateralDAI: formatEther(collateralDAI),
          collateralUSDC: formatEther(collateralUSDC),
          fixedPrice: formatEther(fixedPrice),
          oraclePrice: oraclePrice,
          priceSource: priceSource,
          isAuthorized: isAuthorized,
          authorizationFeeUSD: formatEther(authorizationFeeUSD),
          mintFeePercent: mintFeePercent.toString(),
        });
      } catch (error) {
        console.error("Overview.js - Error fetching CUSD/CUSDMint data:", error);
      }

      // DEUS data
      try {
        const signer = await provider.getSigner();
        const deusContract = new ethers.Contract(deusAddress, DEUSABI, signer);

        const [deusBalance, deusTotalSupply, fixedPrice, eurUsdFeedAddress] = await Promise.all([
          deusContract.balanceOf(account).catch(err => {
            console.error("Overview.js - Error fetching DEUS balance:", err);
            return ethers.toBigInt(0);
          }),
          deusContract.totalSupply().catch(err => {
            console.error("Overview.js - Error fetching DEUS total supply:", err);
            return ethers.toBigInt(0);
          }),
          deusContract.DEUS_PRICE().catch(err => {
            console.error("Overview.js - Error fetching DEUS fixed price:", err);
            return ethers.toBigInt(0);
          }),
          deusContract.eurUsdFeed().catch(err => {
            console.error("Overview.js - Error fetching DEUS eurUsdFeed:", err);
            return ethers.ZeroAddress;
          }),
        ]);

        console.log("Overview.js - DEUS Balance (raw):", deusBalance.toString());
        console.log("Overview.js - DEUS Total Supply (raw):", deusTotalSupply.toString());
        console.log("Overview.js - DEUS Fixed Price (raw):", fixedPrice.toString());
        console.log("Overview.js - DEUS eurUsdFeed Address:", eurUsdFeedAddress);

        let oraclePrice = "Není dostupná";
        let priceSource = "Fixed";

        if (eurUsdFeedAddress !== ethers.ZeroAddress) {
          const chainlinkFeed = new ethers.Contract(eurUsdFeedAddress, ChainlinkPriceFeedABI, provider);
          const [latestPrice, decimals] = await Promise.all([
            chainlinkFeed.latestAnswer().catch(err => {
              console.error("Overview.js - Error fetching Chainlink latest price:", err);
              return ethers.toBigInt(0);
            }),
            chainlinkFeed.decimals().catch(err => {
              console.error("Overview.js - Error fetching Chainlink decimals:", err);
              return 8;
            }),
          ]);
          console.log("Overview.js - Chainlink Latest Price (raw):", latestPrice.toString());
          console.log("Overview.js - Chainlink Decimals:", decimals.toString());

          const priceInUSD = Number(latestPrice) / Number(ethers.parseUnits("1", decimals));
          oraclePrice = priceInUSD > 0 ? (1 / priceInUSD).toFixed(2) : "Není dostupná";
          priceSource = priceInUSD > 0 ? "Oracle" : "Fixed";
        }

        setDEUSData({
          balance: formatEther(deusBalance),
          totalSupply: formatEther(deusTotalSupply),
          fixedPrice: formatEther(fixedPrice),
          oraclePrice: oraclePrice,
          priceSource: priceSource,
        });
      } catch (error) {
        console.error("Overview.js - Error fetching DEUS data:", error);
      }

      // Price history for Crown
      try {
        const crownContractWithSigner = crownContract.connect(await provider.getSigner());
        const deploymentBlock = 0; // Load from contract deployment
        const latestBlock = await provider.getBlockNumber();
        console.log("Overview.js - Fetching price history from block", deploymentBlock, "to", latestBlock);

        const eventsToFetch = [
          "TokenPriceUpdated",
          "TokensBought",
          "TokensSold",
          "LoanCreated",
          "LoanRepaid",
          "TokensBoughtWithERC20",
          "TokensSoldForERC20",
          "UseOraclePriceUpdated",
        ];

        const eventPromises = eventsToFetch.map(async (eventName) => {
          try {
            const events = await crownContract.queryFilter(crownContract.getEvent(eventName), deploymentBlock, latestBlock);
            console.log(`Overview.js - ${eventName} events:`, events);
            return events.map(event => ({ ...event, type: eventName }));
          } catch (err) {
            console.error(`Overview.js - Error fetching ${eventName} events:`, err);
            return [];
          }
        });

        const allEvents = (await Promise.all(eventPromises)).flat().sort(
          (a, b) => a.blockNumber - b.blockNumber || a.transactionIndex - b.transactionIndex
        );

        let currentPrice = ethers.parseEther("0.00001"); // Start at initial contract price
        let useOraclePrice = false;
        const history = [];

        const initialBlock = await provider.getBlock(deploymentBlock);
        history.push({
          timestamp: initialBlock.timestamp,
          price: Number(formatEther(currentPrice)),
        });

        const priceAdjustment = ethers.parseEther("0.000000001");

        for (const event of allEvents) {
          const block = await provider.getBlock(event.blockNumber);
          const timestamp = block.timestamp;

          if (event.type === "UseOraclePriceUpdated") {
            useOraclePrice = event.args.newStatus;
            if (useOraclePrice) {
              currentPrice = ethers.parseEther("0.00004");
            }
          } else if (event.type === "TokenPriceUpdated") {
            currentPrice = event.args.newPrice;
          } else if (!useOraclePrice) {
            let tokenAmount = ethers.toBigInt(0);
            if (event.type === "TokensBought" || event.type === "TokensBoughtWithERC20") {
              tokenAmount = event.args.tokensBought;
              currentPrice = currentPrice + (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
            } else if (event.type === "LoanCreated") {
              tokenAmount = event.args.loanAmount;
              currentPrice = currentPrice + (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
            } else if (event.type === "TokensSold" || event.type === "TokensSoldForERC20") {
              tokenAmount = event.args.tokenAmount;
              const priceDecrease = (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
              currentPrice = currentPrice > priceDecrease ? currentPrice - priceDecrease : ethers.toBigInt(0);
            } else if (event.type === "LoanRepaid") {
              tokenAmount = event.args.totalRepayment;
              const priceDecrease = (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
              currentPrice = currentPrice > priceDecrease ? currentPrice - priceDecrease : ethers.toBigInt(0);
            }
          }

          history.push({
            timestamp,
            price: Number(formatEther(currentPrice)),
          });
        }

        const labels = history.map(h => new Date(h.timestamp * 1000).toLocaleString());
        const values = history.map(h => h.price);
        console.log("Overview.js - Price history:", { labels, values });
        setPriceHistory({ labels, values });

        // Event listeners for real-time updates
        const updatePriceHistory = async (eventType, event) => {
          const block = await provider.getBlock(event.blockNumber);
          const timestamp = block.timestamp;
          let newPrice = currentPrice;
          let tokenAmount = ethers.toBigInt(0);

          if (eventType === "UseOraclePriceUpdated") {
            useOraclePrice = event.args.newStatus;
            if (useOraclePrice) {
              newPrice = ethers.parseEther("0.00004");
            }
          } else if (eventType === "TokenPriceUpdated") {
            newPrice = event.args.newPrice;
          } else if (!useOraclePrice) {
            if (["TokensBought", "TokensBoughtWithERC20"].includes(eventType)) {
              tokenAmount = event.args.tokensBought;
              newPrice = newPrice + (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
            } else if (eventType === "LoanCreated") {
              tokenAmount = event.args.loanAmount;
              newPrice = newPrice + (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
            } else if (["TokensSold", "TokensSoldForERC20"].includes(eventType)) {
              tokenAmount = event.args.tokenAmount;
              const priceDecrease = (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
              newPrice = newPrice > priceDecrease ? newPrice - priceDecrease : ethers.toBigInt(0);
            } else if (eventType === "LoanRepaid") {
              tokenAmount = event.args.totalRepayment;
              const priceDecrease = (priceAdjustment * tokenAmount) / ethers.parseUnits("1", 18);
              newPrice = newPrice > priceDecrease ? newPrice - priceDecrease : ethers.toBigInt(0);
            }
          }

          currentPrice = newPrice;
          setPriceHistory(prev => ({
            labels: [...prev.labels, new Date(timestamp * 1000).toLocaleString()],
            values: [...prev.values, Number(formatEther(newPrice))],
          }));
        };

        crownContractWithSigner.on("TokenPriceUpdated", (newPrice, event) => updatePriceHistory("TokenPriceUpdated", event));
        crownContractWithSigner.on("TokensBought", (buyer, ethAmountInWei, tokensBought, feeInTokens, ownerFee, rewardPoolFee, validatorsFee, contractEthBalance, event) => updatePriceHistory("TokensBought", event));
        crownContractWithSigner.on("TokensSold", (seller, tokenAmount, ethReceived, feeInTokens, ownerFee, rewardPoolFee, validatorsFee, contractEthBalance, event) => updatePriceHistory("TokensSold", event));
        crownContractWithSigner.on("LoanCreated", (borrower, collateralWei, loanAmount, event) => updatePriceHistory("LoanCreated", event));
        crownContractWithSigner.on("LoanRepaid", (borrower, collateralReturned, totalRepayment, event) => updatePriceHistory("LoanRepaid", event));
        crownContractWithSigner.on("TokensBoughtWithERC20", (buyer, token, tokenAmount, tokensBought, event) => updatePriceHistory("TokensBoughtWithERC20", event));
        crownContractWithSigner.on("TokensSoldForERC20", (seller, token, tokenAmount, erc20Received, event) => updatePriceHistory("TokensSoldForERC20", event));
        crownContractWithSigner.on("UseOraclePriceUpdated", (owner, newStatus, event) => updatePriceHistory("UseOraclePriceUpdated", event));

        return () => {
          crownContractWithSigner.removeAllListeners("TokenPriceUpdated");
          crownContractWithSigner.removeAllListeners("TokensBought");
          crownContractWithSigner.removeAllListeners("TokensSold");
          crownContractWithSigner.removeAllListeners("LoanCreated");
          crownContractWithSigner.removeAllListeners("LoanRepaid");
          crownContractWithSigner.removeAllListeners("TokensBoughtWithERC20");
          crownContractWithSigner.removeAllListeners("TokensSoldForERC20");
          crownContractWithSigner.removeAllListeners("UseOraclePriceUpdated");
        };
      } catch (error) {
        console.error("Overview.js - Error fetching price history:", error);
        setPriceHistory({
          labels: ["12.6.2025 20:32:35", "14.6.2025 14:43:59", "16.6.2025 14:43:59"],
          values: [0.00001, 0.000018, 0.000016],
        });
      }
    };
    fetchData();
  }, [provider, account]);

  return (
    <div>
      <h2>
        <FaChartLine className="icon" /> Portfolio Overview
      </h2>

      {network && (
        <div className="info" style={{ marginBottom: '2rem' }}>
          <strong>Network:</strong> {network.name} (Chain ID: {network.chainId.toString()})
        </div>
      )}

      {contractExists === false && (
        <div className="error">
          ⚠️ Warning: No contract found at DEUS address {deusAddress}. Please check the address.
        </div>
      )}

      {/* Price Tickers */}
      <div className="ticker-container">
        <div className="ticker-item">
          <div className="ticker-label">Crown (CRW)</div>
          <div className="ticker-value">
            <FaCoins style={{ fontSize: '1.25rem', color: 'var(--gold)' }} />
            {parseFloat(crownData.tokenPriceBase).toFixed(9)} ETH
          </div>
          <div className="ticker-change positive">
            <MdTrendingUp /> {crownData.priceSource}
          </div>
        </div>

        <div className="ticker-item">
          <div className="ticker-label">CUSD</div>
          <div className="ticker-value">
            <FaDollarSign style={{ fontSize: '1.25rem', color: 'var(--silver)' }} />
            {parseFloat(cusdData.fixedPrice).toFixed(6)} USD
          </div>
          <div className="ticker-change">
            {cusdData.priceSource}
          </div>
        </div>

        <div className="ticker-item">
          <div className="ticker-label">DEUS</div>
          <div className="ticker-value">
            <FaEuroSign style={{ fontSize: '1.25rem', color: 'var(--steel-blue)' }} />
            {parseFloat(deusData.fixedPrice).toFixed(6)} EUR
          </div>
          <div className="ticker-change">
            {deusData.priceSource}
          </div>
        </div>

        <div className="ticker-item">
          <div className="ticker-label">Total Value Locked</div>
          <div className="ticker-value">
            <FaLock style={{ fontSize: '1.25rem', color: 'var(--accent-green)' }} />
            {(parseFloat(crownData.reserveETH) + parseFloat(cusdData.collateralETH)).toFixed(4)} ETH
          </div>
          <div className="ticker-change positive">
            <MdTrendingUp /> Reserves
          </div>
        </div>
      </div>

      <div className="overview-container">
        {/* Crown section */}
        <div className="overview-section">
          <h3>
            <FaCoins style={{ color: 'var(--gold)' }} /> Crown (CRW)
          </h3>
          <div className="overview-grid overview-grid-crown">
            <div className="stat-card">
              <div className="stat-label">
                <FaWallet style={{ marginRight: '0.5rem' }} /> Your Balance
              </div>
              <div className="stat-value">
                {parseFloat(crownData.balance).toFixed(6)}
                <span className="stat-unit">CRW</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">
                <FaLock style={{ marginRight: '0.5rem' }} /> Contract Balance
              </div>
              <div className="stat-value">
                {parseFloat(crownData.contractBalance).toFixed(6)}
                <span className="stat-unit">ETH</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Supply</div>
              <div className="stat-value">
                {parseFloat(crownData.totalSupply).toFixed(6)}
                <span className="stat-unit">CRW</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Sold</div>
              <div className="stat-value">
                {parseFloat(crownData.totalSoldTokens).toFixed(6)}
                <span className="stat-unit">CRW</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Reserve ETH</div>
              <div className="stat-value">
                {parseFloat(crownData.reserveETH).toFixed(6)}
                <span className="stat-unit">ETH</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Reserve Tokens</div>
              <div className="stat-value">
                {parseFloat(crownData.reserveTokens).toFixed(6)}
                <span className="stat-unit">CRW</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Collateral Reserve</div>
              <div className="stat-value">
                {parseFloat(crownData.collateralReserveETH).toFixed(6)}
                <span className="stat-unit">ETH</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Reward Pool</div>
              <div className="stat-value">
                {parseFloat(crownData.rewardPool).toFixed(6)}
                <span className="stat-unit">CRW</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">
                <FaExchangeAlt style={{ marginRight: '0.5rem' }} /> Token Price
              </div>
              <div className="stat-value">
                {parseFloat(crownData.tokenPriceBase).toFixed(9)}
                <span className="stat-unit">ETH</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Oracle Price</div>
              <div className="stat-value" style={{ fontSize: '1.25rem' }}>
                {crownData.oraclePrice !== "Není dostupná z kontraktu"
                  ? parseFloat(crownData.oraclePrice).toFixed(6) + " ETH"
                  : crownData.oraclePrice}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Price Source</div>
              <div className="stat-value" style={{ fontSize: '1.25rem' }}>
                {crownData.priceSource}
              </div>
            </div>
          </div>
        </div>

        {/* CUSD section */}
        <div className="overview-section cusd">
          <h3 className="cusd">
            <FaDollarSign style={{ color: 'var(--silver)' }} /> CUSD Stablecoin
          </h3>
          <div className="overview-grid overview-grid-cusd">
            <div className="stat-card">
              <div className="stat-label">
                <FaWallet style={{ marginRight: '0.5rem' }} /> Your Balance
              </div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.balance).toFixed(6)}
                <span className="stat-unit">CUSD</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Supply</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.totalSupply).toFixed(6)}
                <span className="stat-unit">CUSD</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">
                <FaLock style={{ marginRight: '0.5rem' }} /> Collateral ETH
              </div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.collateralETH).toFixed(6)}
                <span className="stat-unit">ETH</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Collateral DAI</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.collateralDAI).toFixed(6)}
                <span className="stat-unit">DAI</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Collateral USDC</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.collateralUSDC).toFixed(6)}
                <span className="stat-unit">USDC</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">
                <FaExchangeAlt style={{ marginRight: '0.5rem' }} /> Fixed Price
              </div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.fixedPrice).toFixed(6)}
                <span className="stat-unit">USD</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Oracle Price</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)', fontSize: '1.25rem' }}>
                {cusdData.oraclePrice !== "Není dostupná z kontraktu"
                  ? parseFloat(cusdData.oraclePrice).toFixed(6) + " USD"
                  : cusdData.oraclePrice}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Price Source</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)', fontSize: '1.25rem' }}>
                {cusdData.priceSource}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Authorized Minter</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)', fontSize: '1.25rem' }}>
                {cusdData.isAuthorized ? "✓ Yes" : "✗ No"}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Authorization Fee</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.authorizationFeeUSD).toFixed(6)}
                <span className="stat-unit">USD</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Mint Fee</div>
              <div className="stat-value" style={{ color: 'var(--silver-light)' }}>
                {parseFloat(cusdData.mintFeePercent).toFixed(6)}
                <span className="stat-unit">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* DEUS section */}
        <div className="overview-section deus">
          <h3 className="deus">
            <FaEuroSign style={{ color: 'var(--steel-blue)' }} /> DEUS Token
          </h3>
          <div className="overview-grid overview-grid-deus">
            <div className="stat-card">
              <div className="stat-label">
                <FaWallet style={{ marginRight: '0.5rem' }} /> Your Balance
              </div>
              <div className="stat-value" style={{ color: 'var(--steel-blue-light)' }}>
                {parseFloat(deusData.balance).toFixed(6)}
                <span className="stat-unit">DEUS</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Supply</div>
              <div className="stat-value" style={{ color: 'var(--steel-blue-light)' }}>
                {parseFloat(deusData.totalSupply).toFixed(6)}
                <span className="stat-unit">DEUS</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">
                <FaExchangeAlt style={{ marginRight: '0.5rem' }} /> Fixed Price
              </div>
              <div className="stat-value" style={{ color: 'var(--steel-blue-light)' }}>
                {parseFloat(deusData.fixedPrice).toFixed(6)}
                <span className="stat-unit">EUR</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Oracle Price (USD/EUR)</div>
              <div className="stat-value" style={{ color: 'var(--steel-blue-light)', fontSize: '1.25rem' }}>
                {deusData.oraclePrice !== "Není dostupná"
                  ? parseFloat(deusData.oraclePrice).toFixed(6) + " EUR"
                  : deusData.oraclePrice}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Price Source</div>
              <div className="stat-value" style={{ color: 'var(--steel-blue-light)', fontSize: '1.25rem' }}>
                {deusData.priceSource}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price History Chart */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <div className="card-header">
          <div className="card-title">
            <FaChartLine /> Price History
          </div>
        </div>
        <div className="card-body">
          <ChartComponent data={priceHistory} />
        </div>
      </div>
    </div>
  );
};

export default Overview;